import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { PageTitle } from 'ui/PageTitle';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { LeaveWithoutSavingModal } from 'ui/LeaveWithoutSavingModal';
import FluidButton from 'ui/FluidButton';
import { BackLink } from 'ui/BackLink';
import { useContentEdit } from './hooks/useContentEdit';
import { ContentEditForm } from './components/ContentEditForm';
import { isInternalUser as isInternalUserSelector, isMarketing } from 'store/modules/auth/selectors';

export const ContentCreatePage = () => {
  const history = useHistory();
  const isInternalUser = useSelector(isInternalUserSelector);
  const isMarketingUser = useSelector(isMarketing);

  // Redirect to home page if user is not an internal user
  useEffect(() => {
    if (!isInternalUser && !isMarketingUser) {
      history.push('/');
    }
  }, [isInternalUser, isMarketingUser, history]);

  const {
    contentEditState,
    isEditedWithoutSaving,
    isLoading,
    error,
    validationErrors,
    handleSave,
    handleCancel,
    updateContentData,
    updateTitle,
    // Main Info section update functions
    updateCrmCompanyUuid,
    updateCategoryUuid,
    updateCategoryEnabled,
    updateDestination,
    updateProductName,
    updateIsExclusive,
    updateProductTypeUuids,
    updateHeroImage,
    addHeroImage,
    // Content Details section update functions
    updateShortDescriptionTitle,
    updateShortDescriptionContent,
    addSection,
    removeSection,
    updateSectionTitle,
    updateSectionContent,
    // Availability & Booking section update functions
    updateCountryCodes,
    resetCountryCodes,
    updateBookingStart,
    updateBookingEnd,
    updateStayPeriodStart,
    updateStayPeriodEnd,
    // Pricing Info section update functions
    updateCurrency,
    updateFullPrice,
    updateDiscountedPrice,
    updateDiscountPercent,
    // Media Assets section update functions
    addPhoto,
    removePhoto,
    // Supporting Documents section update functions
    addDocument,
    removeDocument,
    // Publishing Settings section update functions
    updateSaveAsDraft,
    updatePublishFor,
    updateActiveFrom,
    updateActiveTo,
    updateContactEmail,
  } = useContentEdit({
    contentUuid: null, // null for create mode
  });

  if (isLoading) {
    return <LoadingBar />;
  }

  if (error) {
    return <ErrorBar />;
  }

  return (
    <div className="marketing-content-create w-auto mx-[20px] crm-desktop:mx-[80px]">
      <BackLink type="internal" href="/content">
        Back to content
      </BackLink>
      <PageTitle primaryText="New Content" />

      <div className="content-create-form mt-[30px]">
        <ContentEditForm
          contentEditState={contentEditState}
          validationErrors={validationErrors}
          isCreateMode={true}
          updateTitle={updateTitle}
          updateCrmCompanyUuid={updateCrmCompanyUuid}
          updateCategoryUuid={updateCategoryUuid}
          updateCategoryEnabled={updateCategoryEnabled}
          updateDestination={updateDestination}
          updateProductName={updateProductName}
          updateIsExclusive={updateIsExclusive}
          updateProductTypeUuids={updateProductTypeUuids}
          updateHeroImage={updateHeroImage}
          addHeroImage={addHeroImage}
          updateShortDescriptionTitle={updateShortDescriptionTitle}
          updateShortDescriptionContent={updateShortDescriptionContent}
          addSection={addSection}
          removeSection={removeSection}
          updateSectionTitle={updateSectionTitle}
          updateSectionContent={updateSectionContent}
          updateCountryCodes={updateCountryCodes}
          resetCountryCodes={resetCountryCodes}
          updateBookingStart={updateBookingStart}
          updateBookingEnd={updateBookingEnd}
          updateStayPeriodStart={updateStayPeriodStart}
          updateStayPeriodEnd={updateStayPeriodEnd}
          updateCurrency={updateCurrency}
          updateFullPrice={updateFullPrice}
          updateDiscountedPrice={updateDiscountedPrice}
          updateDiscountPercent={updateDiscountPercent}
          addPhoto={addPhoto}
          removePhoto={removePhoto}
          addDocument={addDocument}
          removeDocument={removeDocument}
          updateSaveAsDraft={updateSaveAsDraft}
          updatePublishFor={updatePublishFor}
          updateActiveFrom={updateActiveFrom}
          updateActiveTo={updateActiveTo}
          updateContactEmail={updateContactEmail}
        />
      </div>

      <div className="actions-bar mt-[30px] flex gap-[15px]">
        <FluidButton type="primary" onClick={handleSave}>
          Save
        </FluidButton>
      </div>

      <LeaveWithoutSavingModal when={isEditedWithoutSaving} />
    </div>
  );
};
